import * as Actions from '../actions/flatFeeActions';
import FlatFee from '../../entities/flatFee';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {getSessionFirmId, getSessionUserId} from '../../lib/picos/sessionIdentity';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type FlatFeeAction = {
    type: string;
    flatFee: FlatFee | null; 
    flatFeeId: string | FlatFee; 
    partial: boolean; 
    lastFetch: number; 
    flatFees: FlatFee[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<FlatFee> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {FlatFee[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<FlatFee> | null, firmId: string, userId: string, updates?: FlatFee[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'flatFee', 'flatFees', data ? backupRows(data) : null, updates)
		  .then(result => {
			  // OWA-786 (CXR-4-001): the worker resolves a typed result for every
			  // path — a resolution is NOT an acknowledgement. Only ok: true means
			  // the backup write is durable; anything else is surfaced, never
			  // swallowed, so a skipped or failed write cannot masquerade as one.
			  if (!result?.ok) console.warn(`⮂ flatFeeReducer: backup write for 'flatFees' did not persist (${result?.reason ?? 'unknown'}): ${result?.message ?? ''}`);
		  })
		  .catch(console.error);
}

/**
 * The identity that names this tab's backup store, or null when this tab has
 * not finished authenticating.
 *
 * OWA-785: this must come from sessionStorage (per tab), never from
 * localStorage (shared by every tab on the origin). With two firms open in two
 * tabs, the localStorage value is whichever firm logged in last, so the other
 * tab persisted its entities into that firm's backup store and they restored
 * into the wrong firm's UI. A partial identity names some other store, so we
 * skip the write rather than misfile it.
 *
 * @returns {{firmId: string, userId: string} | null}
 */
function storageIdentity(): {firmId: string; userId: string} | null {
	const firmId = getSessionFirmId();
	const userId = getSessionUserId();

	if (!firmId || !userId) {
		if (logging) console.debug('⮂ flatFeeReducer: no session identity, skipping backup write');
		return null;
	}

	return {firmId, userId};
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<FlatFee>} data
 * @param {FlatFee[]} [updates]
 * @returns {ArrayMap<FlatFee>}
 */
function updateStorage(data: ArrayMap<FlatFee>, updates?: FlatFee[]): ArrayMap<FlatFee> {
	if (logging) console.debug('⮂ flatFeeReducer: updateStorage');

	if (updates && updates.length === 0) return data;

	const identity = storageIdentity();
	if (!identity) return data;

	if (updates) callStorageWorker(null, identity.firmId, identity.userId, updates);
	else callStorageWorker(data, identity.firmId, identity.userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {FlatFee[]} [updates]
 * @returns {ArrayMap<FlatFee>}
 */
function updateStorageNoReturn(updates?: FlatFee[]): void {
	if (logging) console.debug('⮂ flatFeeReducer: updateStorageNoReturn');

	if (!updates || updates.length === 0) return;

	const identity = storageIdentity();
	if (!identity) return;

	callStorageWorker(null, identity.firmId, identity.userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {FlatFee[]} [data]
 * @returns {ArrayMap<FlatFee>}
 */
function createState(data?: FlatFee[] | ArrayMap<FlatFee>): ArrayMap<FlatFee> {
	if (!data) return ArrayMapRegistry.clone<FlatFee>('flatFee', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<FlatFee>('flatFee', data.map(e => e));
    else return ArrayMapRegistry.clone<FlatFee>('flatFee', data);
}

// Whether deleted flatFees keep their rows (schema includeDeletedInResults). OWA-68
const retainDeleted = false;

/**
 * The full backup snapshot of a collection: one entry per ID. A row when there is one (deleted rows of a
 * type that keeps them included); otherwise a deletion record for an ID whose row was removed. OWA-68
 *
 * @param {ArrayMap<FlatFee>} data
 * @returns {Array<FlatFee | FlatFeeDeletionRecord>}
 */
function backupRows(data: ArrayMap<FlatFee>): Array<FlatFee | FlatFeeDeletionRecord> {
	const records: FlatFeeDeletionRecord[] = data.deletedVersionEntries()
		.filter(([id]) => !data.has(id))
		.map(([id, addedOn]) => ({flatFeeId: id, addedOn, status: 'D', deletionRecord: true}));

	return records.length ? [...data.values(), ...records] : data.values();
}

/**
 * A deletion record read back from the offline backup: evidence that a flatFee was deleted at a
 * version, never a live row. The storage worker writes one for a deleted flatFee whose type does
 * not keep deleted rows. OWA-68
 */
type FlatFeeDeletionRecord = {flatFeeId: string; addedOn: number; status: 'D'; deletionRecord: true};

const isDeletionRecord = (o: unknown): o is FlatFeeDeletionRecord => (o as FlatFeeDeletionRecord)?.deletionRecord === true;

/**
 * Apply one incoming version of a flatFee to a collection the caller owns (a clone), under the one
 * acceptance rule every path uses (OWA-68):
 * - older than the stored row: rejected
 * - an active version equal to a kept deleted row: rejected (it yields only to a strictly newer one)
 * - no stored row, and at or below the recorded deletion version: rejected
 * - an accepted deleted version records its deletion version; the row is kept as a deleted row when
 *   this type keeps deleted rows, otherwise it is removed
 * - an accepted active version is stored (a newer one supersedes the deletion version)
 *
 * @param {ArrayMap<FlatFee>} st
 * @param {FlatFee | FlatFeeDeletionRecord} incoming
 * @returns {boolean} whether the version was accepted; only accepted versions go to the backup
 */
function applyVersion(st: ArrayMap<FlatFee>, incoming: FlatFee | FlatFeeDeletionRecord): boolean {
	const id = isDeletionRecord(incoming) ? incoming.flatFeeId : incoming.id;
	const current = st.get(id);

	if (current) {
		if (incoming.addedOn < current.addedOn) return false;
		// A kept deleted row yields only to a strictly newer active version, like a deletion version
		if (current.status === 'D' && incoming.status !== 'D' && incoming.addedOn === current.addedOn) return false;
	}
	else {
		const deletedAt = st.deletedVersion(id);
		if (deletedAt !== undefined && incoming.addedOn <= deletedAt) return false;
	}

	if (incoming.status === 'D') {
		st.recordDeletion(id, incoming.addedOn);
		if (retainDeleted && !isDeletionRecord(incoming)) st.set(incoming);
		else if (current) st.delete(current);
	}
	else st.set(incoming as FlatFee);

	return true;
}

/**
 * A new indexed collection holding the rows and deletion versions of the given one. Fetch paths build
 * from this instead of cloning, because the initial store state is a bare ArrayMap without the
 * schema's indexes. OWA-68
 *
 * @param {ArrayMap<FlatFee>} from
 * @returns {ArrayMap<FlatFee>}
 */
function carryOver(from: ArrayMap<FlatFee>): ArrayMap<FlatFee> {
	const next = createState(from);
	from.deletedVersionEntries().forEach(([id, version]) => next.recordDeletion(id, version));
	return next;
}

function flatFees(state: ArrayMap<FlatFee>, action: FlatFeeAction): ArrayMap<FlatFee> {
	if (!state) state = createState();

	let st: ArrayMap<FlatFee>;

	switch (action.type) {
		// action.flatFee - the new value of flatFee with id action.id
		// action.flatFeeId - id of the target, used if new value is null
		case Actions.FLATFEE_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ flatFeeReducer: FETCH_ONE_SUCCESS', action);

			if (action.flatFee === null) return state;

			// Only for a flatFee this collection knows (a row, or a deletion version), and only when the
			// fetched version is accepted (OWA-68).
			if (state.has(action.flatFeeId) || state.deletedVersion(action.flatFee.id) !== undefined) {
				st = state.clone();
				if (!applyVersion(st, action.flatFee)) return state;

				updateStorageNoReturn([action.flatFee]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.flatFees
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.flatFees
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.flatFees
			 !partial, !stateEmpty, fetchEmpty	-> D return flatFees in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge flatFees in state newer than those in action.flatFees
		 */
		case Actions.FLATFEE_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.flatFees.length === 0;
			// A deletion record from the backup is evidence only: never constructed into an entity (OWA-68)
			const fetched: Array<FlatFee | FlatFeeDeletionRecord> = action.flatFees.map(flatFee => isDeletionRecord(flatFee) ? flatFee : new FlatFee({...flatFee, persisted: true}));
			const fetchedIds = new Set(fetched.map(flatFee => isDeletionRecord(flatFee) ? flatFee.flatFeeId : flatFee.id));

			// A full fetch is the whole dataset, so a persisted active row it omits is dropped (deleted rows,
			// deletion versions and unsaved local rows stay). OWA-68
			const dropOmitted = (st: ArrayMap<FlatFee>) => {
				[...st.values()].forEach(row => {
					if (!fetchedIds.has(row.id) && row.persisted && row.status !== 'D') st.delete(row);
				});
			};

			if (logging) console.debug(`⮂ flatFeeReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} flatFees with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ flatFeeReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Build a new indexed state that keeps any deletion versions (the visible state is
					// empty, but a deletion version can still reject an older row). OWA-68
					st = carryOver(state);
					fetched.forEach(flatFee => applyVersion(st, flatFee));

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ flatFeeReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<FlatFee> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: FlatFee[] = [];

				// Partials include deleted rows for every entity; applyVersion keeps or removes them and
				// records their deletion version (OWA-68). Only accepted versions go to the backup.
				fetched.forEach(flatFee => {
					if (applyVersion(st, flatFee)) toUpdate.push(flatFee as FlatFee);
					else if (logging) console.debug('⮂ flatFeeReducer: partialMerge: rejected older version of flatFee');
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ flatFeeReducer: empty: flatFees', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Drop the persisted active rows; deleted rows, deletion versions and unsaved local rows
				// stay (OWA-68)
				st = carryOver(state);
				dropOmitted(st);

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ flatFeeReducer: fullMerge: flatFees', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Start from the current rows and deletion versions, apply each fetched version under the one
				// rule, then drop persisted active rows the full fetch omitted (OWA-68). A fetched row replaces
				// a local one unless the local one is strictly newer, so a stale local (persisted or not) never
				// shadows a newer server row (OWA-7 #1), and a newer row that arrived while the fetch was in
				// flight (for example a socket update during startup restore) is kept.
				st = carryOver(state);
				fetched.forEach(flatFee => applyVersion(st, flatFee));
				dropOmitted(st);

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.FLATFEE_FETCH_ONE_FAILURE:
			console.error('FLATFEE_FETCH_ONE_FAILURE', action.flatFeeId, action.err);

			// No update to storage required
			return state;

		case Actions.FLATFEE_FETCH_FAILURE:
			console.error('FLATFEE_FETCH_FAILURE', action.flatFeeId, action.err);

			// No update to storage required
			return state;

		case Actions.FLATFEE_RESTORE: {
		    let flatFee = new FlatFee({...action.flatFee});
            st = state.clone();

            // One acceptance rule for every incoming version (OWA-68)
            if (!applyVersion(st, flatFee)) return state;

            return updateStorage(st, [flatFee]);
        }
            
		case Actions.FLATFEE_SAVE: {
		    // console.debug('⮂ FLATFEE_SAVE reducer', action.flatFee?.id);
		    
			// clone the flatFee
			let flatFee = new FlatFee({...action.flatFee});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new flatFee in the state, under the one acceptance rule (OWA-68). A rejected
			// version changes nothing and is not written to the backup.
			if (!applyVersion(st, flatFee)) return state;

			return updateStorage(st, [flatFee]);
		}

		case Actions.FLATFEE_SAVE_MANY: {
		    if (logging) console.debug('⮂ FLATFEE_SAVE_MANY reducer', action.flatFees.map(flatFee => flatFee.id).join(','));
		    
			// clone the flatFees
			let toProcess = action.flatFees.map(flatFee => new FlatFee(flatFee));

			st = state.clone();
			// state.clear();

			const updates: FlatFee[] = [];

			toProcess.forEach(flatFee => {
				// Add or update the new flatFee in the state, under the one acceptance rule (OWA-68)
				if (applyVersion(st, flatFee)) updates.push(flatFee);
				else if (logging) console.debug('⮂ FLATFEE_SAVE_MANY: rejected older version of flatFee');
			});

			return updateStorage(st, updates);
		}

		case Actions.FLATFEE_REMOVE: {
			// clone the flatFee; a remove is a deleted version (the remove saga stamps it 'D' and a new
			// addedOn)
			const flatFee = new FlatFee({...action.flatFee, status: 'D'});

			// Remove from UI under the one acceptance rule: the deletion version is recorded, and the row
			// is removed, or kept as a deleted row for a type that keeps them (OWA-68)
			st = state.clone();
			if (!applyVersion(st, flatFee)) return state;

			return updateStorage(st, [flatFee]);
		}

		case Actions.FLATFEE_RESTORE_FAILURE:
		case Actions.FLATFEE_SAVE_FAILURE: {
		    console.warn('FLATFEE_SAVE_FAILURE reducer', action.flatFee?.id);
		    if (!action.flatFee?.id) {
				console.warn('FLATFEE_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const flatFee = ensure(FlatFee, action.flatFee);
			if (!flatFee) {
				console.error('Could not find flatFee after call to save');
				return state;
			}

			st = state.clone();

			const _flatFeeRef = state.get(flatFee.id);

			if (_flatFeeRef && (_flatFeeRef.addedOn === flatFee.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _flatFee = new FlatFee({..._flatFeeRef});
				_flatFee.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_flatFee.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.update('flatFee', flatFee.addedOn, {fatalError: _flatFee.fatalError}).then(r => {
						if (r.isErr()) console.error(`⮂ flatFeeReducer: failed to save flatFee with fatalError info: ${r.unwrapErr().message}`);
						else if (r.unwrap() === 0) {
							// Non-document 4xx handlers may remove the row before dispatch.
							// Preserve the historical rejected-operation queue behavior.
							idb.add('flatFee', _flatFee).then(saved => {
								if (saved.isErr()) console.error('Could not restore rejected flatFee: ' + saved.unwrapErr().message);
							}).catch(err => console.error('Could not restore rejected flatFee: ' + err.message));
						}
						else console.debug('⮂ flatFeeReducer: saved flatFee with fatalError info');
					}).catch(err => console.error(`⮂ flatFeeReducer: failed to save flatFee with fatalError info: ${err.message}`));

				st.set(_flatFee);
			}

			return updateStorage(st);
		}

		case Actions.FLATFEE_SAVE_MANY_FAILURE: {
		    console.warn('FLATFEE_SAVE_MANY_FAILURE reducer', action.flatFees.map(flatFee => flatFee.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.FLATFEE_RESTORE_SUCCESS:
		case Actions.FLATFEE_SAVE_SUCCESS: {
			if (!action.flatFee?.id) {
				console.warn('FLATFEE_SAVE_SUCCESS reducer');
                return state;
			}

			const flatFeeRef = state.get(action.flatFee?.id);
			if (!flatFeeRef) {
				console.warn('Could not find flatFee after call to save');
                return state;
			}

			st = state.clone();

			// The echoed version goes through the one acceptance rule (OWA-68). When it is older than the
			// current row, only the current row's persisted flag changes, and the current row (never the
			// rejected echo) is what the backup receives.
			if (applyVersion(st, action.flatFee)) return updateStorage(st, [action.flatFee]);

			if (flatFeeRef.persisted) return state;

			// Create a new instance to avoid mutating the entity shared with old state
			const flatFee = new FlatFee({...flatFeeRef, persisted: true});
			st.set(flatFee);

			return updateStorage(st, [flatFee]);
		}

		case Actions.FLATFEE_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ FLATFEE_SAVE_MANY_SUCCESS reducer', action.flatFees.map(flatFee => flatFee.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const flatFees: FlatFee[] = [];

			action.flatFees.forEach(e => {
				const flatFeeRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!flatFeeRef) console.warn('Could not find flatFee after call to save');
				// The echoed version goes through the one acceptance rule (OWA-68)
				else if (applyVersion(st, e)) flatFees.push(e);
				else if (!flatFeeRef.persisted) {
					// Older echo: only the current row's persisted flag changes, and the current row is
					// what the backup receives. Create a new instance to avoid mutating the entity shared
					// with old state.
					const flatFee = new FlatFee({...flatFeeRef, persisted: true});
					st.set(flatFee);
					flatFees.push(flatFee);
				}
			});

			return updateStorage(st, flatFees);
		}

		case Actions.FLATFEE_REMOVE_FAILURE: {
		    console.warn('FLATFEE_REMOVE_FAILURE action', action.flatFee?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.FLATFEE_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.FLATFEE_RESTORE_MANY: {
			if (logging) console.debug('⮂ FLATFEE_RESTORE_MANY reducer', action.flatFees.map(flatFee => flatFee.id).join(','));

			const toProcess = action.flatFees.map(flatFee => new FlatFee({...flatFee}));
			st = state.clone();

			const updates: FlatFee[] = [];

			// One acceptance rule for every incoming version (OWA-68): a deleted version removes the row and
			// records its deletion version, or keeps a deleted row for a type that keeps them.
			toProcess.forEach(flatFee => {
				if (applyVersion(st, flatFee)) updates.push(flatFee);
				else if (logging) console.debug('⮂ FLATFEE_RESTORE_MANY: rejected older version of flatFee');
			});

			return updateStorage(st, updates);
		}

		case Actions.FLATFEE_REMOVE_MANY: {
			if (logging) console.debug('⮂ FLATFEE_REMOVE_MANY reducer', action.flatFees.map(flatFee => flatFee.id).join(','));

			// A socket delete is a deleted version, under the one acceptance rule (OWA-68)
			const toProcess = action.flatFees.map(flatFee => new FlatFee({...flatFee, status: 'D'}));
			st = state.clone();

			const updates: FlatFee[] = [];

			toProcess.forEach(flatFee => {
				if (applyVersion(st, flatFee)) updates.push(flatFee);
				else if (logging) console.debug('⮂ FLATFEE_REMOVE_MANY: rejected older version of flatFee');
			});

			return updateStorage(st, updates);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default flatFees;